import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { EligibleRelatedMinorDto, RelatedMinorGender, RelatedMinorRelationship } from '../../../api/api.g';
import Label, { LabelType } from '../Label';
import ModalContainer from '../ModalContainer';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';

export interface RelatedMinorSelectionPopupProps {
  minors?: EligibleRelatedMinorDto[];
  onConfirm(selectedRelatedMinorId: string): void;
}

const RelatedMinorSelectionPopup = (props: RelatedMinorSelectionPopupProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const [selectedId, setSelectedId] = useState<string>();

  useEffect(() => {
    setSelectedId(undefined);
  }, [props.minors]);

  const genderLabel = (gender?: RelatedMinorGender) => {
    switch (gender) {
      case RelatedMinorGender.Male:
        return t('related-minor-gender-male');
      case RelatedMinorGender.Female:
        return t('related-minor-gender-female');
      case RelatedMinorGender.NonBinary:
        return t('related-minor-gender-non-binary');
      default:
        return '';
    }
  };

  const relationshipLabel = (relationship?: RelatedMinorRelationship) => {
    switch (relationship) {
      case RelatedMinorRelationship.Child:
        return t('related-minor-relationship-child');
      case RelatedMinorRelationship.Sibling:
        return t('related-minor-relationship-sibling');
      case RelatedMinorRelationship.LegalGuardian:
        return t('related-minor-relationship-legal-guardian');
      case RelatedMinorRelationship.OtherRelatedFamily:
        return t('related-minor-relationship-other-related-family');
      default:
        return '';
    }
  };

  return (
    <ModalContainer buttonText={t('confirm')} confirmDisabled={!selectedId} onConfirm={() => selectedId && props.onConfirm(selectedId)}>
      <View>
        <Label variant={LabelType.titleLargeNoCapsPrimary}>{t('select-related-minor')}</Label>
        <Label style={{ marginTop: 8 }}>{t('related-minor-selection-required')}</Label>
        <View style={{ marginTop: 16 }}>
          {(props.minors ?? []).map((minor) => {
            const isSelected = minor.id === selectedId;

            return (
              <Pressable
                key={minor.id}
                onPress={() => setSelectedId(minor.id)}
                style={{
                  borderWidth: 1,
                  borderColor: isSelected ? theme.background.sea : theme.borderColorLight,
                  backgroundColor: isSelected ? theme.background.lightSea : 'transparent',
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 8,
                }}
              >
                <Label variant={LabelType.titleSmall}>{`${minor.firstName ?? ''} ${minor.lastName ?? ''}`.trim()}</Label>
                <Label style={{ marginTop: 4 }}>{[genderLabel(minor.gender), relationshipLabel(minor.relationship)].filter(Boolean).join(' - ')}</Label>
                {!!minor.birthDate && <Label style={{ marginTop: 4 }}>{minor.birthDate.toLocaleDateString()}</Label>}
              </Pressable>
            );
          })}
        </View>
        {!props.minors?.length && <Label>{t('related-minor-required')}</Label>}
      </View>
    </ModalContainer>
  );
};

export default RelatedMinorSelectionPopup;
